import { zodResolver } from '@hookform/resolvers/zod'
import LoadingButton from '@mui/lab/LoadingButton'
import { Box, Checkbox, Divider, FormControlLabel, Link, TextField, Typography } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { TwoFactorAuthType } from '@/api/types'

const formSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Code must be 6 digits'),
  rememberBrowser: z.boolean(),
})

type FormData = z.infer<typeof formSchema>

export interface VerifyMFACodeFormProps {
  method: TwoFactorAuthType
  otpSentTo?: string | null
  allowUpdateRememberBrowser: boolean
  verifyCodeButtonText: string
  onVerify: (data: FormData) => void
  onResendClick?: VoidFunction
  onChangeMethodClick?: VoidFunction
  onUseRecoveryCode?: VoidFunction
}

export function VerifyMFACodeForm({
  method,
  otpSentTo,
  allowUpdateRememberBrowser,
  verifyCodeButtonText,
  onVerify,
  onResendClick,
  onChangeMethodClick,
  onUseRecoveryCode,
}: VerifyMFACodeFormProps) {
  const isAuthenticator = method === TwoFactorAuthType.Authenticator

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: '',
      rememberBrowser: false,
    },
  })

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      form.handleSubmit(onVerify)()
    }
  }

  const showFooterLinks = !!onChangeMethodClick || !!onUseRecoveryCode

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        {isAuthenticator ? (
          <Typography variant="body2">Enter the 6-digit code from your authenticator app.</Typography>
        ) : (
          otpSentTo && <Typography variant="body2">A verification code was sent to {otpSentTo}</Typography>
        )}
        {!isAuthenticator && onResendClick && (
          <Link component="button" type="button" typography="body2" color="secondary.dark" onClick={onResendClick}>
            Resend code
          </Link>
        )}
      </Box>
      <Controller
        name="code"
        control={form.control}
        render={({ field }) => (
          <TextField
            {...field}
            placeholder="Enter code"
            inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 }}
            error={!!form.formState.errors.code}
            helperText={form.formState.errors.code?.message}
            autoFocus
            onKeyDown={handleKeyDown}
          />
        )}
      />
      <LoadingButton
        loading={form.formState.isSubmitting}
        color="primary"
        variant="contained"
        size="large"
        fullWidth
        onClick={form.handleSubmit(onVerify)}
      >
        {verifyCodeButtonText}
      </LoadingButton>
      {allowUpdateRememberBrowser && (
        <Controller
          name="rememberBrowser"
          control={form.control}
          render={({ field }) => (
            <FormControlLabel
              label="Remember this device"
              control={<Checkbox color="primary" checked={field.value} onChange={field.onChange} />}
            />
          )}
        />
      )}
      {showFooterLinks && (
        <>
          <Divider />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {onChangeMethodClick && (
              <Link
                component="button"
                type="button"
                typography="body2"
                color="secondary.dark"
                onClick={onChangeMethodClick}
              >
                Change method
              </Link>
            )}
            {onUseRecoveryCode && (
              <Link
                component="button"
                type="button"
                typography="body2"
                color="secondary.dark"
                onClick={onUseRecoveryCode}
              >
                Use a recovery code
              </Link>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}
